import React, { useContext, useEffect, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { ReactComponent as EditNoteIcon } from '../../../../assets/images/edit-note.svg';
import { OrganisationContext } from '../../../../context/organisationContext';
import { FormVersionLogWrapper } from '../../../../styles/components/formDetails/form-details.styled';
import Button from '../../../Elements/button/button';
import FormVersionDetailsHeader from '../form-version-details-header';
import { useTranslation } from 'react-i18next';
import Menu from '../../../common/menu';
import { getSigners, updateSigners } from '../../../../store/features/formsSlice';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import Signer from '../signer';
import update from 'immutability-helper';

const FormVersionLog = () => {
  const { id, versionId } = useParams();
  const { setModal, hasPermission } = useContext(OrganisationContext);
  const { formDetails, formVersionDetails, signers } = useSelector(state => state.forms);
  const { created_on, published_on, archived_on } = formVersionDetails || {};

  const [signerItems, setSignerItems] = useState(signers);
  const [changed, setChanged] = useState(false);

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const AutomationLogHeader = [
    {
      name: t('NAME'),
    },
  ];

  const handleFormEdit = () => {
    setModal({
      type: 'edit-form-version',
      content: {
        formId: id,
        formVersionId: versionId,
        prizedraw_client: formDetails?.prizedraw_client,
      },
    });
  };

  useEffect(() => {
    dispatch(
      getSigners({
        id: versionId,
      }),
    );
  }, [versionId]);

  const onAddSigner = () => {
    setModal({
      type: 'add-signer',
      content: {
        updatedSigner: null,
        versionId: versionId,
        top: true,
        onSuccess: () => refreshSignersList(),
      },
    });
  };
  const onUpdateSigner = signer => {
    setModal({
      type: 'add-signer',
      content: {
        updatedSigner: signer,
        versionId: versionId,
        top: true,
        onSuccess: () => refreshSignersList(),
      },
    });
  };

  const onDeleteSigner = signer => {
    setModal({
      type: 'add-signer',
      content: {
        deletedSigner: signer,
        versionId: versionId,
        top: true,
        onSuccess: () => refreshSignersList(),
      },
    });
  };

  const refreshSignersList = () => {
    dispatch(
      getSigners({
        id: versionId,
      }),
    );
  };

  useEffect(() => {
    setSignerItems(signers);
  }, [signers]);

  const moveCard = useCallback((dragIndex, hoverIndex) => {
    setSignerItems(prevCards =>
      update(prevCards, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, prevCards[dragIndex]],
        ],
      }),
    );
    setChanged(true);
  }, []);

  const renderCard = useCallback((item, index) => {
    return (
      <Signer
        key={item.id}
        id={item.id}
        index={index}
        signer={item}
        onUpdateSigner={onUpdateSigner}
        onDeleteSigner={onDeleteSigner}
        formVersionDetails={formVersionDetails}
        moveCard={moveCard}
      />
    );
  }, []);

  const onSave = () => {
    dispatch(updateSigners({ versionId: versionId, request: signerItems }));
    setChanged(false);
  };

  return (
    <FormVersionLogWrapper className="flex-column items-start justify-start w-full h-full">
      <FormVersionDetailsHeader
        title={'Signers'}
        actionContent={
          hasPermission('FORMS_CONFIGURE') ? (
            <div className="flex items-center">
              {changed && (
                <div className="mr-2">
                  <Button
                    {...{
                      label: 'Save',
                      size: 'small',
                      borderRadius: '8px',
                      className: 'primary',
                      width: '100px',
                    }}
                    disabled={created_on && published_on && !archived_on}
                    onClick={onSave}
                  />
                </div>
              )}

              <Button
                {...{
                  label: 'Add',
                  size: 'small',
                  borderRadius: '8px',
                  className: 'primary',
                  width: '100px',
                }}
                disabled={created_on && published_on && !archived_on}
                onClick={onAddSigner}
              />
              <Button
                className="default-secondary ml-6"
                label={'Form editor'}
                size={'medium'}
                icon={<EditNoteIcon height={16} width={16} />}
                onClick={() => handleFormEdit()}
                disabled={created_on && published_on && !archived_on}
              />
            </div>
          ) : (
            <></>
          )
        }
      />
      <div className="border-top flex-column flex-1 w-full">
        <div className="flex-column py-2 divider-bottom w-full header-container">
          {AutomationLogHeader.map(({ name, value, ignoreRightBorder }) => (
            <div
              key={value}
              className={`flex items-center header-item-container`}
              onClick={() => handleSorting(sort_by)}>
              <span className="medium-text lighter-text font-12 ml-4">{name}</span>
            </div>
          ))}
        </div>
        <div className="flex-column flex-1 overflow-scroll w-full">
          <DndProvider backend={HTML5Backend}>{signerItems?.map((item, i) => renderCard(item, i))}</DndProvider>
        </div>
      </div>
    </FormVersionLogWrapper>
  );
};

export default FormVersionLog;
